// Times are shown in the plant's time zone, with the zone named, so everyone looking at a
// plant talks about the same clock. The API itself is always UTC.
import type { Datapoint, SyncStatus } from './api';

const toMs = (t: string | number) => (typeof t === 'number' ? t : Date.parse(t));

const formatters = new Map<string, Intl.DateTimeFormat>();
function formatter(tz: string, opts: Intl.DateTimeFormatOptions): Intl.DateTimeFormat {
  const key = `${tz}|${JSON.stringify(opts)}`;
  let f = formatters.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat('en-GB', { timeZone: tz, ...opts });
    formatters.set(key, f);
  }
  return f;
}

/** "14:05 CEST" today, "12 Oct 14:05 CEST" otherwise. */
export function fmtTime(t: string | number, tz: string, { seconds = false, zone = true } = {}): string {
  const ms = toMs(t);
  const day = (x: number) => formatter(tz, { year: 'numeric', month: '2-digit', day: '2-digit' }).format(x);
  const sameDay = day(ms) === day(Date.now());
  const opts: Intl.DateTimeFormatOptions = {
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
    ...(seconds && { second: '2-digit' }),
    ...(!sameDay && { day: 'numeric', month: 'short' }),
    ...(zone && { timeZoneName: 'short' }),
  };
  return formatter(tz, opts).format(ms);
}

export function fmtDate(t: string | number, tz: string): string {
  return formatter(tz, { day: 'numeric', month: 'short', year: 'numeric' }).format(toMs(t));
}

export function fmtAgo(t: string | number, now = Date.now()): string {
  const s = Math.max(0, Math.round((now - toMs(t)) / 1000));
  if (s < 45) return 'just now';
  if (s < 90 * 60) return `${Math.max(1, Math.round(s / 60))} min ago`;
  if (s < 36 * 3600) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} days ago`;
}

export function fmtDuration(seconds: number): string {
  if (seconds < 60) return `${Math.round(seconds)} s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  if (seconds < 86400) {
    const h = Math.floor(seconds / 3600);
    const m = Math.round((seconds % 3600) / 60);
    return m ? `${h} h ${m} min` : `${h} h`;
  }
  const d = Math.floor(seconds / 86400);
  const h = Math.round((seconds % 86400) / 3600);
  return h ? `${d} d ${h} h` : `${d} d`;
}

export function fmtNumber(v: number): string {
  const abs = Math.abs(v);
  const digits = abs >= 1000 ? 0 : abs >= 100 ? 1 : abs >= 1 ? 2 : 3;
  return new Intl.NumberFormat('en-GB', { maximumFractionDigits: digits }).format(v);
}

/** "oxygenPurity" → "Oxygen purity" */
export function humanize(s: string): string {
  const words = s.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_-]+/g, ' ').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export const datapointLabel = (d: Pick<Datapoint, 'displayName' | 'metric'>) => d.displayName || humanize(d.metric);

type Limits = { low?: number; high?: number };
export type Tone = 'normal' | 'warning' | 'critical';

const outside = (v: number, l?: Limits) => !!l && ((l.low !== undefined && v < l.low) || (l.high !== undefined && v > l.high));

/** Value labels for states and booleans ("0": "Manual"), from the manifest. */
export function valueLabels(d: Pick<Datapoint, 'meta'>): Record<string, string> | null {
  const values = d.meta.values;
  if (values && typeof values === 'object') return values as Record<string, string>;
  if (String(d.meta.dataType ?? '').toLowerCase() === 'boolean') return { '0': 'Off', '1': 'On' };
  return null;
}

export function fmtValue(d: Pick<Datapoint, 'meta' | 'unit'>, value: number | null, text: string | null): { text: string; tone: Tone } {
  if (value === null) return { text: text ?? '–', tone: 'normal' };
  const labels = valueLabels(d);
  if (labels) return { text: labels[String(value)] ?? fmtNumber(value), tone: 'normal' };
  const t = d.meta.thresholds as { warning?: Limits; critical?: Limits } | undefined;
  const tone: Tone = outside(value, t?.critical) ? 'critical' : outside(value, t?.warning) ? 'warning' : 'normal';
  return { text: `${fmtNumber(value)}${d.unit ? ` ${d.unit}` : ''}`, tone };
}

export type Freshness = 'live' | 'delayed' | 'offline' | 'never';

/** From sync status: is data coming in? */
export function freshness(sync: SyncStatus, now = Date.now()): { state: Freshness; label: string; detail: string } {
  if (!sync.lastHeartbeatAt) return { state: 'never', label: 'No data yet', detail: 'This plant has not sent anything.' };
  const heartbeatAge = (now - Date.parse(sync.lastHeartbeatAt)) / 1000;
  const lag = sync.lagSeconds ?? 0;
  if (heartbeatAge > 15 * 60) return { state: 'offline', label: 'Offline', detail: `Last contact ${fmtAgo(sync.lastHeartbeatAt, now)}` };
  if (lag > 15 * 60) return { state: 'delayed', label: 'Catching up', detail: `Data is ${fmtDuration(lag)} behind` };
  return { state: 'live', label: 'Live', detail: sync.dataUntil ? `Data up to ${fmtAgo(sync.dataUntil, now)}` : '' };
}

const SEVERITY_ORDER = ['critical', 'high', 'medium', 'low', 'info'];
export const severityRank = (s: string | null | undefined) => {
  const i = SEVERITY_ORDER.indexOf(s ?? '');
  return i < 0 ? SEVERITY_ORDER.length : i;
};

/** "2026-10-14T14:05" in the plant's zone, for datetime-local inputs. */
export function toZonedInput(ms: number, tz: string): string {
  const p = Object.fromEntries(
    formatter(tz, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
      .formatToParts(ms)
      .map((x) => [x.type, x.value]),
  );
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}

/** The inverse: a datetime-local value meant in the plant's zone, as epoch ms. */
export function fromZonedInput(value: string, tz: string): number {
  const asUtc = Date.parse(`${value}:00Z`);
  const offset = Date.parse(`${toZonedInput(asUtc, tz)}:00Z`) - asUtc;
  return asUtc - offset;
}
